import {
  $generateNodesFromSerializedNodes,
  $getHtmlContent,
  $getLexicalContent,
  copyToClipboard,
} from '@lexical/clipboard';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $createNodeSelection,
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isNodeSelection,
  $isRootOrShadowRoot,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  COPY_COMMAND,
  createCommand,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  LexicalCommand,
  LexicalEditor,
  type LexicalNode,
  NodeKey,
  PASTE_COMMAND,
} from 'lexical';
import { useEffect } from 'react';

import {
  $createImageNode,
  $isImageNode,
  ImageNode,
  ImagePayload,
} from '../../wysiwyg/nodes/ImageNode';

export const INSERT_IMAGE_COMMAND: LexicalCommand<ImagePayload> =
  createCommand('INSERT_IMAGE_COMMAND');

export function ImagePlugin(): JSX.Element | null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([ImageNode])) {
      throw new Error('ImagePlugin: ImageNode not registered on editor');
    }

    const removeDocumentKeydownListener = registerDocumentKeydownListener(editor);
    const removeDocumentCopyListener = registerDocumentCopyListener(editor);
    const removeDocumentPasteListener = registerDocumentPasteListener(editor);

    return mergeRegister(
      removeDocumentKeydownListener,
      removeDocumentCopyListener,
      removeDocumentPasteListener,
      editor.registerCommand<ImagePayload>(
        INSERT_IMAGE_COMMAND,
        (payload) => {
          const imageNode = $createImageNode(payload);
          const paragraphNode = $createParagraphNode();
          insertImageAtRootSelection(imageNode, paragraphNode);
          const imageNodeKey = imageNode.getKey();
          const selection = $createNodeSelection();
          selection.add(imageNodeKey);
          $setSelection(selection);
          scrollImageIntoView(editor, imageNodeKey);
          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      editor.registerCommand<KeyboardEvent>(
        KEY_DELETE_COMMAND,
        removeSelectedImageFromCommand,
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand<KeyboardEvent>(
        KEY_BACKSPACE_COMMAND,
        removeSelectedImageFromCommand,
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand<ClipboardEvent | KeyboardEvent | null>(
        COPY_COMMAND,
        (event) => {
          if (!getSelectedImageNode()) {
            return false;
          }

          if (isClipboardEvent(event)) {
            return writeSelectedImageToClipboard(editor, event);
          }

          void copyToClipboard(editor, null);
          return true;
        },
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand<ClipboardEvent | InputEvent | KeyboardEvent>(
        PASTE_COMMAND,
        (event) => {
          if (!(event instanceof ClipboardEvent) || !editor.isEditable()) {
            return false;
          }

          return insertCopiedImagesAfterSelection(editor, event);
        },
        COMMAND_PRIORITY_HIGH,
      ),
    );
  }, [editor]);

  return null;
}

function removeSelectedImageFromCommand(event: KeyboardEvent) {
  const imageNode = getSelectedImageNode();
  if (!imageNode) {
    return false;
  }

  event.preventDefault();
  imageNode.remove();
  return true;
}

function registerDocumentKeydownListener(editor: LexicalEditor) {
  if (typeof document === 'undefined') {
    return () => undefined;
  }

  const removeSelectedImageFromDocument = (event: KeyboardEvent) => {
    if (event.defaultPrevented || !isEditorEventTarget(editor, event)) {
      return;
    }

    if (
      !editor.isEditable() ||
      !isImageDeleteKeyboardEvent(event) ||
      !hasSelectedImageNode(editor)
    ) {
      return;
    }

    editor.update(() => {
      const imageNode = getSelectedImageNode();
      if (!imageNode) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      imageNode.remove();
    });
  };

  document.addEventListener('keydown', removeSelectedImageFromDocument);
  return () => {
    document.removeEventListener('keydown', removeSelectedImageFromDocument);
  };
}

function registerDocumentCopyListener(editor: LexicalEditor) {
  if (typeof document === 'undefined') {
    return () => undefined;
  }

  const copySelectedImageFromDocument = (event: ClipboardEvent) => {
    if (event.defaultPrevented || !isEditorEventTarget(editor, event)) {
      return;
    }

    editor.getEditorState().read(() => {
      writeSelectedImageToClipboard(editor, event);
    });
  };

  document.addEventListener('copy', copySelectedImageFromDocument);
  return () => {
    document.removeEventListener('copy', copySelectedImageFromDocument);
  };
}

function registerDocumentPasteListener(editor: LexicalEditor) {
  if (typeof document === 'undefined') {
    return () => undefined;
  }

  const pasteCopiedImageFromDocument = (event: ClipboardEvent) => {
    if (event.defaultPrevented || !editor.isEditable() || !isEditorEventTarget(editor, event)) {
      return;
    }

    editor.update(() => {
      insertCopiedImagesAfterSelection(editor, event);
    });
  };

  document.addEventListener('paste', pasteCopiedImageFromDocument);
  return () => {
    document.removeEventListener('paste', pasteCopiedImageFromDocument);
  };
}

function hasSelectedImageNode(editor: LexicalEditor) {
  let hasSelectedImage = false;
  editor.getEditorState().read(() => {
    hasSelectedImage = getSelectedImageNode() !== null;
  });
  return hasSelectedImage;
}

function isImageDeleteKeyboardEvent(event: KeyboardEvent) {
  return event.key === 'Delete' || event.key === 'Backspace';
}

function isEditorEventTarget(editor: LexicalEditor, event: Event) {
  const rootElement = editor.getRootElement();
  const target = event.target;
  if (!rootElement || !(target instanceof Node)) {
    return false;
  }

  return target === document.body || target === rootElement || rootElement.contains(target);
}

function getSelectedImageNode() {
  const selection = $getSelection();
  if (!$isNodeSelection(selection)) {
    return null;
  }

  const nodes = selection.getNodes();
  if (nodes.length !== 1) {
    return null;
  }

  const [node] = nodes;
  return $isImageNode(node) ? node : null;
}

function writeSelectedImageToClipboard(editor: LexicalEditor, event: ClipboardEvent) {
  const clipboardData = event.clipboardData;
  if (!clipboardData || !getSelectedImageNode()) {
    return false;
  }

  event.preventDefault();

  const htmlString = $getHtmlContent(editor);
  if (htmlString) {
    clipboardData.setData('text/html', htmlString);
  }

  const lexicalString = $getLexicalContent(editor);
  if (lexicalString) {
    clipboardData.setData('application/x-lexical-editor', lexicalString);
  }

  clipboardData.setData('text/plain', $getSelection()?.getTextContent() ?? '');
  return true;
}

function insertCopiedImagesAfterSelection(editor: LexicalEditor, event: ClipboardEvent) {
  const imageNode = getSelectedImageNode();
  if (!imageNode) {
    return false;
  }

  const copiedImageNodes = getCopiedImageNodes(editor, event.clipboardData);
  if (copiedImageNodes.length === 0) {
    return false;
  }
  const lastCopiedImageNode = copiedImageNodes[copiedImageNodes.length - 1];
  if (!lastCopiedImageNode) {
    return false;
  }

  event.preventDefault();

  let previousNode: LexicalNode = imageNode;
  for (const copiedImageNode of copiedImageNodes) {
    previousNode.insertAfter(copiedImageNode);
    previousNode = copiedImageNode;
  }

  const selection = $createNodeSelection();
  selection.add(lastCopiedImageNode.getKey());
  $setSelection(selection);
  return true;
}

function getCopiedImageNodes(editor: LexicalEditor, clipboardData: DataTransfer | null) {
  const lexicalString = clipboardData?.getData('application/x-lexical-editor');
  if (!lexicalString) {
    return [];
  }

  const payload = parseClipboardPayload(lexicalString);
  if (!payload || payload.namespace !== editor._config.namespace) {
    return [];
  }

  return $generateNodesFromSerializedNodes(payload.nodes).filter($isImageNode);
}

function parseClipboardPayload(lexicalString: string) {
  try {
    const payload: unknown = JSON.parse(lexicalString);
    if (!isClipboardPayload(payload)) {
      return null;
    }

    return payload;
  } catch {
    return null;
  }
}

function isClipboardPayload(payload: unknown): payload is {
  namespace: string;
  nodes: Parameters<typeof $generateNodesFromSerializedNodes>[0];
} {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const { namespace, nodes } = payload as { namespace?: unknown; nodes?: unknown };
  return typeof namespace === 'string' && Array.isArray(nodes);
}

function isClipboardEvent(event: ClipboardEvent | KeyboardEvent | null): event is ClipboardEvent {
  return typeof ClipboardEvent !== 'undefined' && event instanceof ClipboardEvent;
}

function insertImageAtRootSelection(
  imageNode: ImageNode,
  paragraphNode: ReturnType<typeof $createParagraphNode>,
) {
  const selection = $getSelection();
  const root = $getRoot();

  if (!selection) {
    root.append(imageNode, paragraphNode);
    return;
  }

  const selectionNodes = selection.getNodes();
  const selectedNode = selectionNodes[selectionNodes.length - 1];
  const topLevelNode = selectedNode ? getTopLevelNode(selectedNode) : null;

  if (!topLevelNode) {
    root.append(imageNode, paragraphNode);
    return;
  }

  if (topLevelNode.getParent() !== root) {
    root.append(imageNode, paragraphNode);
    return;
  }

  topLevelNode.insertAfter(imageNode);
  imageNode.insertAfter(paragraphNode);
}

function getTopLevelNode(node: LexicalNode) {
  let topLevelNode: LexicalNode = node;
  let parent = topLevelNode.getParent();

  while (parent && !$isRootOrShadowRoot(parent)) {
    topLevelNode = parent;
    parent = topLevelNode.getParent();
  }

  return parent ? topLevelNode : null;
}

function scrollImageIntoView(editor: LexicalEditor, nodeKey: NodeKey, attempts = 3) {
  if (typeof window === 'undefined') {
    return;
  }

  window.requestAnimationFrame(() => {
    const element = editor.getElementByKey(nodeKey);
    if (element) {
      element.scrollIntoView({ block: 'center', inline: 'nearest' });
      return;
    }

    if (attempts > 0) {
      scrollImageIntoView(editor, nodeKey, attempts - 1);
    }
  });
}
